import React, { useRef, useEffect, useReducer } from 'react';

interface ItemIconProps {
  atlas: string;
  frame: string;
  size: number;
}

/**
 * Draws the given Phaser atlas frame onto the canvas.
 * Returns true if the draw succeeded, false if the texture isn't available yet.
 */
function drawFrame(canvas: HTMLCanvasElement, atlas: string, frame: string, size: number): boolean {
  const phaserGame = window.phaserGame;
  if (!phaserGame?.textures) return false;

  const texture = phaserGame.textures.get(atlas);
  if (!texture || texture.key === '__MISSING') return false;

  const phaserFrame = texture.get(frame) as Phaser.Textures.Frame;
  if (!phaserFrame) return false;

  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  ctx.clearRect(0, 0, size, size);

  const img = phaserFrame.source.image as HTMLImageElement | HTMLCanvasElement;
  const scale = size / phaserFrame.realWidth;

  if (!phaserFrame.rotated) {
    ctx.drawImage(
      img,
      phaserFrame.cutX, phaserFrame.cutY, phaserFrame.cutWidth, phaserFrame.cutHeight,
      phaserFrame.x * scale, phaserFrame.y * scale,
      phaserFrame.cutWidth * scale, phaserFrame.cutHeight * scale,
    );
  } else {
    // Frame stored rotated 90° in atlas. Rotate canvas CCW to display correctly.
    const cw = phaserFrame.cutWidth * scale;
    const ch = phaserFrame.cutHeight * scale;
    const cx = (phaserFrame.x + phaserFrame.cutWidth / 2) * scale;
    const cy = (phaserFrame.y + phaserFrame.cutHeight / 2) * scale;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(Math.PI * 1.5);
    // In atlas the packed region has swapped dims: height=cutWidth, width=cutHeight
    ctx.drawImage(img, phaserFrame.cutX, phaserFrame.cutY, phaserFrame.cutHeight, phaserFrame.cutWidth, -ch / 2, -cw / 2, ch, cw);
    ctx.restore();
  }

  return true;
}

/**
 * Renders an item icon by extracting its frame from a Phaser texture atlas onto a canvas.
 * Requires the atlas to be loaded in the Phaser game instance (preloaded in MapScene).
 * If the atlas isn't loaded yet, listens for Phaser's addtexture event and retries.
 */
export const ItemIcon: React.FC<ItemIconProps> = ({ atlas, frame, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (drawFrame(canvas, atlas, frame, size)) return;

    // Atlas not available yet — listen for Phaser to finish loading it.
    const phaserGame = window.phaserGame;
    if (!phaserGame?.textures) return;

    const onTextureAdded = (key: string) => {
      if (key === atlas) {
        const c = canvasRef.current;
        if (c) drawFrame(c, atlas, frame, size);
        phaserGame.textures.off('addtexture', onTextureAdded);
        forceRender();
      }
    };

    phaserGame.textures.on('addtexture', onTextureAdded);
    return () => {
      phaserGame.textures.off('addtexture', onTextureAdded);
    };
  }, [atlas, frame, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ imageRendering: 'pixelated', display: 'block' }}
    />
  );
};
